/**
 * RandomLetterSwap — glitch-free implementation
 *
 * No AnimatePresence, no popLayout, no layout shifts.
 * Each character position is a single persistent motion.span that:
 *   1. Animates OUT  (y: 0 → -6, opacity: 1 → 0)  in ~70ms
 *   2. Swaps the character text
 *   3. Animates IN   (y: 6 → 0, opacity: 0 → 1)   in ~100ms
 *
 * This is equivalent to what motion-primitives does, but more robust
 * in plain React (CRA) without Next.js / SSR.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useAnimation } from 'motion/react';

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&';
const rand = () => CHARS[Math.floor(Math.random() * CHARS.length)];

// ── Single character cell ────────────────────────────────────────────────────
function Char({ char }) {
  const controls = useAnimation();
  const [visible, setVisible] = useState(char);
  const charRef = useRef(char);

  useEffect(() => {
    // Skip on first render
    if (charRef.current === char) return;
    charRef.current = char;

    let live = true;

    async function run() {
      // OUT
      await controls.start({
        y: -6,
        opacity: 0,
        transition: { duration: 0.07, ease: 'easeIn' },
      });
      if (!live) return;

      // SWAP text mid-air
      setVisible(char);

      // IN from below
      controls.set({ y: 6, opacity: 0 });
      controls.start({
        y: 0,
        opacity: 1,
        transition: { duration: 0.1, ease: 'easeOut' },
      });
    }

    run();
    return () => { live = false; };
  }, [char]);

  return (
    <motion.span
      animate={controls}
      style={{ display: 'inline-block', willChange: 'transform, opacity' }}
    >
      {visible === ' ' ? '\u00A0' : visible}
    </motion.span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export function RandomLetterSwap({
  label = '',
  className = '',
  style = {},
  staggerDuration = 0.025,
}) {
  const original = label.split('');
  const [displayed, setDisplayed] = useState(original);
  const timers = useRef([]);
  const isAnimating = useRef(false);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const onEnter = useCallback(() => {
    if (isAnimating.current) return;
    isAnimating.current = true;
    clear();

    const stepMs = staggerDuration * 1000;
    const len = original.length;

    original.forEach((_, i) => {
      // swap to random
      const t1 = setTimeout(() => {
        setDisplayed((prev) => {
          const next = [...prev];
          next[i] = rand();
          return next;
        });
      }, i * stepMs);

      // resolve to original
      const t2 = setTimeout(() => {
        setDisplayed((prev) => {
          const next = [...prev];
          next[i] = original[i];
          return next;
        });
        if (i === len - 1) {
          isAnimating.current = false;
        }
      }, i * stepMs + stepMs);

      timers.current.push(t1, t2);
    });
  }, [label, staggerDuration]);

  const onLeave = useCallback(() => {
    // don't interrupt if still mid-animation — let it finish
  }, []);

  return (
    <motion.span
      className={className}
      style={{ display: 'inline-flex', ...style }}
      onHoverStart={onEnter}
      onHoverEnd={onLeave}
    >
      {displayed.map((char, i) => (
        <Char key={i} char={char} />
      ))}
    </motion.span>
  );
}

export default RandomLetterSwap;
